import { Box, Divider, LinearProgress, Stack, Tooltip, Typography, type TypographyProps, styled, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ActionButton, Dot, Flex, HStack, colors } from "@zfp-akademie-a-s/zfp-design";
import { EditIcon, InfoIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { getGetGoalModelingDetailQueryKey, useGetFinancialReserveGoal, useGetHousingPropertyGoal } from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  type Amount,
  type CoverageSection,
  type GoalOverview,
  GoalOverviewSubType,
  GoalOverviewType,
  type TimeDuration,
} from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { formatPercentage } from "Infrastructure/Utils/NumberUtils";
import { type FC, useContext, useState } from "react";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { EditGoalDrawer } from "../Goals/GoalManagement/EditGoalDrawer";
import { GoalIcon } from "../_Shared/Goals/GoalIcon";
import { getTimeDurationFormatted } from "../_Shared/helpers";

type Props = {
  readonly goalId: string;
  readonly name: string;
  readonly interestRate: number;
  readonly feeValue: number;
  readonly goalDuration: TimeDuration | null;
  readonly targetAmount: Amount;
  readonly coverageSection: CoverageSection;
  readonly rawGoalOverview: GoalOverview;
  readonly inflationValue: number;
};

type TitleValueProps = TypographyProps & {
  title: string;
  value: string;
  color?: string;
  fontWeight?: string;
  valueVariant?: TypographyProps["variant"];
};

const COVERAGE_THRESHOLD = 100;

const TitleValue = ({ title, value, color = "inherit", fontWeight = "inherit", valueVariant = "body2", ...rest }: TitleValueProps) => {
  return (
    <Stack>
      <Typography variant="body2" color="secondary" {...rest}>
        {title}
      </Typography>
      <Typography variant={valueVariant} color={color} fontWeight={fontWeight} {...rest}>
        {value}
      </Typography>
    </Stack>
  );
};

const Container = styled(Box)`
  background: ${colors.backgroundSecondary};
  border-radius: ${(props) => props.theme.spacing(Spacing.XL)};
`;

const _getColor = (percentage: number) => {
  if (percentage < 20) return "error";
  if (percentage < 80) return "warning";
  return "success";
};

export const GoalSummaryCard: FC<Props> = ({
  name,
  interestRate,
  feeValue,
  goalDuration,
  targetAmount,
  coverageSection,
  rawGoalOverview,
  goalId,
  inflationValue,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useResource();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);

  const isGoalTypeFinancialReserve = rawGoalOverview.type === GoalOverviewType.financial_reserve;
  const isGoalTypeHousingProperty =
    rawGoalOverview.type === GoalOverviewType.housing && rawGoalOverview.subType === GoalOverviewSubType.housing_property;

  const canLoadFinancialReserveGoal = isGoalTypeFinancialReserve && Boolean(analysisId && versionId && rawGoalOverview.guid);
  const canLoadHousingPropertyGoal = isGoalTypeHousingProperty && Boolean(analysisId && versionId && rawGoalOverview.guid);

  const { data: financialReserveGoal } = useGetFinancialReserveGoal(analysisId, versionId, rawGoalOverview.guid, {
    query: { enabled: canLoadFinancialReserveGoal },
  });

  const { data: housingPropertyGoal } = useGetHousingPropertyGoal(analysisId, versionId, rawGoalOverview.guid, {
    query: { enabled: canLoadHousingPropertyGoal },
  });

  const [editDrawerOpen, setEditDrawerOpen] = useState(false);

  const requiredAmount = Math.round(coverageSection.requiredAmount ?? 0);
  const coveredAmount = Math.round(coverageSection.coveredAmount ?? 0);
  const requiredOneTimeAmount = Math.round(coverageSection.requiredOneTimeAmount ?? 0);
  const coveredOneTimeAmount = Math.round(coverageSection.coveredOneTimeAmount ?? 0);
  const missingAmount = Math.round(coverageSection.missingAmount ?? 0);
  const completedPercentage = Math.round(coverageSection.coveredPercent ?? 0);
  const color = _getColor(completedPercentage);

  const currentFinancialReserve = financialReserveGoal?.data.currentFinancialReserve?.value ?? null;

  let currentPropertySavings = null;
  if (housingPropertyGoal) {
    currentPropertySavings = housingPropertyGoal.modeling.investCurrentSavings ? (housingPropertyGoal.data.currentSavings?.value ?? 0) : 0;
  }

  const handleEditDrawerOpen = () => {
    setEditDrawerOpen(true);
  };
  const handleEditDrawerClose = () => {
    setEditDrawerOpen(false);
    queryClient.invalidateQueries({ queryKey: getGetGoalModelingDetailQueryKey(analysisId, versionId, goalId) });
  };

  return (
    <>
      <Container mb={Spacing.XXXL} mr={Spacing.M}>
        <Stack padding={Spacing.L} spacing={Spacing.M}>
          <Box height={theme.spacing(Spacing.XXL)} display="flex" justifyContent="space-between" alignItems="center">
            <Flex gap={Spacing.M} alignItems={"center"}>
              <Typography color="primary.main">
                <GoalIcon type={rawGoalOverview.type} />
              </Typography>
              <Typography variant="body2" fontWeight={600} color={theme.palette.primary.main}>
                {name}
              </Typography>
            </Flex>
            <ActionButton label={t(Resources.Common.Edit)} icon={<EditIcon />} onClick={handleEditDrawerOpen} />
          </Box>
          <Divider />
          <HStack justifyContent="space-between" px={Spacing.M}>
            <TitleValue
              title={t(Resources.AnalysisDetail.Goals.TargetAmount)}
              value={formatCurrency(targetAmount.value, targetAmount.currencyCode)}
            />
            <TitleValue
              title={t(Resources.AnalysisDetail.Modeling.TimeHorizon)}
              value={getTimeDurationFormatted(goalDuration ?? { years: 0, months: 0 }, t)}
              textAlign={"right"}
            />
          </HStack>

          <HStack justifyContent="space-between" px={Spacing.M}>
            {isGoalTypeFinancialReserve && (
              <TitleValue
                title={t(Resources.AnalysisDetail.Goals.CurrentEmergencyFundAmount)}
                value={currentFinancialReserve == null ? "—" : formatCurrency(currentFinancialReserve, targetAmount.currencyCode)}
              />
            )}
            {isGoalTypeHousingProperty && (
              <TitleValue
                title={t(Resources.AnalysisDetail.Goals.CurrentSavingsAmount)}
                value={currentPropertySavings == null ? "—" : formatCurrency(currentPropertySavings, targetAmount.currencyCode)}
              />
            )}
            {rawGoalOverview.rentAmount && (
              <TitleValue
                title={t(Resources.AnalysisDetail.Goals.MonthlyPensionAmount)}
                value={formatCurrency(rawGoalOverview.rentAmount.value, rawGoalOverview.rentAmount.currencyCode)}
              />
            )}
          </HStack>

          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={Spacing.M} divider={<Dot />}>
            <Stack direction="row" justifyContent="flex-end" alignItems="flex-end" gap={Spacing.S} sx={{ whiteSpace: "nowrap" }}>
              <Typography variant="caption" color={colors.textSecondary} fontWeight={400}>
                {t(Resources.AnalysisDetail.Goals.InterestRate)}
              </Typography>
              <Typography variant="caption" color={colors.textPrimary} fontWeight={500}>
                {formatPercentage(interestRate)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="flex-end" alignItems="flex-end" gap={Spacing.S} sx={{ whiteSpace: "nowrap" }}>
              <Typography variant="caption" color={colors.textSecondary} fontWeight={400}>
                {t(Resources.AnalysisDetail.Goals.InflationRate)}
              </Typography>
              <Typography variant="caption" color={colors.textPrimary} fontWeight={500}>
                {formatPercentage(inflationValue)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="flex-end" alignItems="flex-end" gap={Spacing.S} sx={{ whiteSpace: "nowrap" }}>
              <Typography variant="caption" color={colors.textSecondary} fontWeight={400}>
                {t(Resources.AnalysisDetail.Goals.Fee)}
              </Typography>
              <Typography variant="caption" color={colors.textPrimary} fontWeight={500}>
                {formatPercentage(feeValue)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Box px={Spacing.M}>
          <Divider />
        </Box>

        <Box padding={Spacing.L}>
          <Typography variant="overline" color="secondary" fontWeight={500} textTransform="uppercase">
            {t(Resources.AnalysisDetail.Modeling.Needed)}
          </Typography>

          <Stack spacing={Spacing.L} mt={Spacing.L}>
            {missingAmount > COVERAGE_THRESHOLD && (
              <Stack spacing={Spacing.L} mt={Spacing.L}>
                <Stack spacing={Spacing.XS}>
                  <Typography variant="body2" px={Spacing.M} fontWeight={"600"}>
                    {t(Resources.AnalysisDetail.Modeling.Monthly)}
                  </Typography>
                  <HStack justifyContent="space-between" px={Spacing.M}>
                    <TitleValue
                      title={t(Resources.AnalysisDetail.Modeling.Covered)}
                      value={formatCurrency(coveredAmount, targetAmount.currencyCode)}
                      color={color}
                      fontWeight={"bold"}
                      valueVariant="body1"
                    />
                    <TitleValue
                      title={t(Resources.AnalysisDetail.Modeling.Needed)}
                      value={formatCurrency(requiredAmount, targetAmount.currencyCode)}
                      textAlign={"right"}
                      fontWeight={"bold"}
                      valueVariant="body1"
                    />
                  </HStack>
                </Stack>
                <Divider
                  sx={{
                    my: 3,
                    typography: "caption",
                    color: "text.secondary",
                    fontWeight: "bold",
                    "&::before, &::after": {
                      borderColor: "divider",
                    },
                    textTransform: "uppercase",
                  }}
                >
                  {t(Resources.Common.Or)}
                </Divider>
                <Stack spacing={Spacing.XS}>
                  <Typography variant="body2" px={Spacing.M} fontWeight={"600"}>
                    {t(Resources.AnalysisDetail.Modeling.OneTime)}
                  </Typography>
                  <HStack justifyContent="space-between" px={Spacing.M}>
                    <TitleValue
                      title={t(Resources.AnalysisDetail.Modeling.Covered)}
                      value={formatCurrency(coveredOneTimeAmount, targetAmount.currencyCode)}
                      color={color}
                      valueVariant="body1"
                      fontWeight={"bold"}
                    />
                    <TitleValue
                      title={t(Resources.AnalysisDetail.Modeling.Needed)}
                      value={formatCurrency(requiredOneTimeAmount, targetAmount.currencyCode)}
                      textAlign={"right"}
                      valueVariant="body1"
                      fontWeight={"bold"}
                    />
                  </HStack>
                </Stack>
              </Stack>
            )}
            <Flex alignItems="center">
              <Box sx={{ flexGrow: 1, mr: Spacing.M }}>
                <LinearProgress
                  color={color}
                  sx={{ height: theme.spacing(Spacing.M), borderRadius: theme.spacing(Radius.M) }}
                  variant="determinate"
                  value={completedPercentage > 100 ? 100 : completedPercentage}
                />
              </Box>

              <Flex alignItems="center">
                <Typography variant="body2" color={color}>
                  {formatPercentage(completedPercentage, 0)}
                </Typography>

                <Tooltip
                  title={
                    <Box>
                      <Flex justifyContent="space-between" gap={Spacing.L}>
                        <Typography variant="caption" color="grey.100">
                          {t(Resources.AnalysisDetail.Modeling.MissingAmount)}
                        </Typography>
                        <Flex gap={Spacing.XS}>
                          <Typography variant="caption" color="grey.50">
                            {formatCurrency(missingAmount, targetAmount.currencyCode)}
                          </Typography>
                        </Flex>
                      </Flex>
                      <Flex justifyContent="space-between" gap={Spacing.L}>
                        <Typography variant="caption" color="grey.100">
                          {t(Resources.AnalysisDetail.Modeling.CurrentCoverage)}
                        </Typography>
                        <Flex gap={Spacing.XS}>
                          <Typography variant="caption" color="grey.50">
                            {formatCurrency(targetAmount.value - missingAmount, targetAmount.currencyCode)}
                          </Typography>
                        </Flex>
                      </Flex>
                    </Box>
                  }
                >
                  <Flex alignItems="center" pl={Spacing.S} sx={{ cursor: "pointer", color: "grey.400", "&:hover": { color: "grey.800" } }}>
                    <InfoIcon sx={{ fontSize: 16 }} />
                  </Flex>
                </Tooltip>
              </Flex>
            </Flex>
          </Stack>
        </Box>
      </Container>
      <EditGoalDrawer open={editDrawerOpen} onClose={handleEditDrawerClose} goal={rawGoalOverview} />
    </>
  );
};
